// This file is part of Invenio-RDM-Records
// Copyright (C) 2020-2023 CERN.
// Copyright (C) 2020-2022 Northwestern University.
//
// Invenio-RDM-Records is free software; you can redistribute it and/or modify it
// under the terms of the MIT License; see LICENSE file for more details.

import _cloneDeep from "lodash/cloneDeep";
import _get from "lodash/get";
import _pick from "lodash/pick";
import _set from "lodash/set";
import { Field } from "./Field";

export class LocationField extends Field {
  /**
   * IMPORTANT: This component is so far only for list subfields, since
   * the use case of a single object with schema has not arisen yet.
   */
  constructor({ fieldpath, schema, deserializedDefault = [], serializedDefault = [] }) {
    super({ fieldpath, deserializedDefault, serializedDefault });
    this.schema = schema;
    this.schemaKeys = Object.keys(this.schema);
  }

  /**
   * Deserialize backend field given by `this.fieldPath` from `serialized`
   * object into format compatible with frontend using `this.schema`.
   * @method
   * @param {object} serialized - in API format
   * @returns {object} deserialized - in frontent format
   */
  deserialize(serialized, defaultLocale) {
    const fieldValues = _get(serialized, this.fieldpath, this.deserializedDefault);
    const deserializedElements = fieldValues.map((value, i) => {
      if (typeof value === "number") {
        return value;
      }
      const keys = this.schemaKeys.map((s) => {
        if (!this.schema[s].fieldpath) return s;
        return this.schema[s].fieldpath;
      });
      let deserializedElement = _pick(value, keys);
      let coordinates = deserializedElement.geometry?.coordinates || [];
      this.schemaKeys.forEach((key) => {
        if (key == "coordinates") {
          deserializedElement = {
            ...deserializedElement,
            geometry: {
              ...deserializedElement.geometry,
              coordinates,
            },
          };
        } else {
          deserializedElement = this.schema[key].deserialize(
            deserializedElement,
            defaultLocale
          );
        }
      });
      // Add __key
      deserializedElement.__key = i;
      return deserializedElement;
    });
    return _set(_cloneDeep(serialized), this.fieldpath, deserializedElements);
  }

  /**
   * Serialize frontend field given by `this.fieldPath` from `deserialized`
   * object into format compatible with backend using `this.schema`.
   * @method
   * @param {object} deserialized - in frontend format
   * @returns {object} serialized - in API format
   *
   */
  serialize(deserialized, defaultLocale) {
    const fieldValues = _get(deserialized, this.fieldpath, this.serializedDefault);
    const toFloat = (arr) => {
      return arr.map((item) => {
        if (Array.isArray(item)) {
          return toFloat(item);
        } else if (typeof item === "string" && !isNaN(item)) {
          return parseFloat(item);
        }
        return item;
      });
    };

    const serializedElements = fieldValues
      .map((value) => {
        if (typeof value === "number") {
          return value;
        }
        if (!isNaN(value) && !isNaN(parseFloat(value))) {
          return parseFloat(value);
        }

        const keys = this.schemaKeys.map((s) => {
          if (!this.schema[s].fieldpath) return s;
          return this.schema[s].fieldpath;
        });

        let serializedElement = _pick(value, keys);

        this.schemaKeys.forEach((key) => {
          if (key === "coordinates") return;
          serializedElement = this.schema[key].serialize(
            serializedElement,
            defaultLocale
          );
        });

        // Check if geometry exists
        if (value?.geometry) {
          let coordinates = toFloat(value.geometry.coordinates);
          serializedElement.geometry = {
            type: value.geometry.type,
            coordinates: coordinates,
          };
        }

        return serializedElement;
      })
      .filter((value) => {
        return value && (value.place || value.description || value.geometry);
      });

    // We need the format not to be this:
    // {place: "Hels", description: "ab", geometry: {type: {id: "Point"}, coordinates: [20, 10]}}
    // But this:
    // {place: "Hels", description: "ab", geometry: {type: "Point", coordinates: [20, 10]}}
    const reSerializedElements = serializedElements.map((field) => {
      return {
        place: field.place,
        description: field.description,
        geometry: field.geometry
          ? { type: field.geometry.type, coordinates: field.geometry.coordinates }
          : undefined, // Keep geometry undefined if not present
      };
    });
    if (reSerializedElements !== null) {
      return _set(_cloneDeep(deserialized), this.fieldpath, reSerializedElements);
    }
    return reSerializedElements;
  }
}
